import { useEffect, useRef, useState } from "react";
import { Camera, CameraOff, Mic, MicOff, PhoneOff, Video, AlertTriangle, Loader2, Users } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const CONN_BADGE = {
  CONNECTED: { dot: "bg-emerald-400 animate-pulse", label: "Connected" },
  CONNECTING: { dot: "bg-amber-400 animate-pulse", label: "Connecting…" },
  RECONNECTING: { dot: "bg-amber-400 animate-pulse", label: "Reconnecting…" },
  DISCONNECTING: { dot: "bg-muted-foreground", label: "Disconnecting…" },
  DISCONNECTED: { dot: "bg-rose-400", label: "Disconnected" },
};

function fmtClock(s) {
  const mm = String(Math.floor((s || 0) / 60)).padStart(2, "0");
  const ss = String(Math.floor((s || 0) % 60)).padStart(2, "0");
  return `${mm}:${ss}`;
}

function RemoteVideoBox({ user }) {
  const boxRef = useRef(null);

  useEffect(() => {
    if (!boxRef.current || !user || !user.videoTrack) return;
    try {
      user.videoTrack.play(boxRef.current, { fit: "cover" });
    } catch (err) {
      console.warn(`[Agora] Error playing video for user ${user.uid}:`, err);
    }
  }, [user?.uid, user?.videoTrack]);

  const uidStr = String(user?.uid || "");
  const roleName =
    uidStr === "1001" || uidStr.startsWith("1001")
      ? "Doctor"
      : uidStr === "2001" || uidStr.startsWith("2001")
      ? "Patient"
      : uidStr === "3001" || uidStr.startsWith("3001")
      ? "Clinic"
      : `Participant ${user.uid}`;

  const hasVideo = !!user?.videoTrack;

  return (
    <div
      id={`remote-user-${user.uid}`}
      data-testid={`consult-remote-video-${user.uid}`}
      className="relative w-full h-full bg-slate-950 rounded-xl border border-white/10 flex items-center justify-center min-h-0 min-w-0 overflow-hidden"
    >
      {hasVideo ? (
        <div ref={boxRef} className="w-full h-full [&>div]:!w-full [&>div]:!h-full [&>video]:!w-full [&>video]:!h-full [&>video]:!object-cover" />
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 text-white/80 p-4">
          <div className="w-20 h-20 rounded-full bg-slate-800 border-2 border-white/20 flex items-center justify-center text-xl font-bold shadow-lg">
            {roleName.charAt(0)}
          </div>
          <div className="flex items-center gap-2 text-xs font-medium bg-black/40 px-3 py-1 rounded-full border border-white/10">
            <CameraOff className="w-3.5 h-3.5 text-rose-400" />
            <span>{roleName} (Camera Off)</span>
          </div>
        </div>
      )}

      <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur px-2.5 py-1 rounded-md text-[11px] text-white/90 font-medium z-10 flex items-center gap-1.5 pointer-events-none">
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        {roleName}
      </div>
    </div>
  );
}

export default function AgoraVideoPanel({ consultationId, startedAt, elapsed, onEndCall, canShow, isCompleted }) {
  const [phase, setPhase] = useState("idle"); // idle | loading | joined | mock | error
  const [errorMsg, setErrorMsg] = useState("");
  const [connState, setConnState] = useState("CONNECTING");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [remoteUsers, setRemoteUsers] = useState([]);

  const clientRef = useRef(null);
  const localTracksRef = useRef({ audio: null, video: null });
  const localContainerRef = useRef(null);
  const joinedRef = useRef(false);
  const myUidRef = useRef(null);

  // Helper to completely stop native MediaStreamTracks to kill the laptop camera hardware light
  const releaseVideoHardware = (track) => {
    if (track) {
      try {
        if (typeof track.getMediaStreamTrack === "function") {
          const raw = track.getMediaStreamTrack();
          if (raw) {
            raw.enabled = false;
            raw.stop();
          }
        }
      } catch (_) {}
      try {
        if (track._mediaStreamTrack) {
          track._mediaStreamTrack.enabled = false;
          track._mediaStreamTrack.stop();
        }
      } catch (_) {}
      try {
        if (track._originMediaStreamTrack) {
          track._originMediaStreamTrack.enabled = false;
          track._originMediaStreamTrack.stop();
        }
      } catch (_) {}
      try {
        if (track._track) {
          track._track.enabled = false;
          track._track.stop();
        }
      } catch (_) {}
      try { track.stop(); } catch (_) {}
      try { track.close(); } catch (_) {}
    }

    if (localContainerRef.current) {
      try {
        const vids = localContainerRef.current.querySelectorAll("video");
        vids.forEach((v) => {
          try { v.pause(); } catch (_) {}
          if (v.srcObject && typeof v.srcObject.getTracks === "function") {
            v.srcObject.getTracks().forEach((vt) => {
              try { vt.enabled = false; } catch (_) {}
              try { vt.stop(); } catch (_) {}
            });
            v.srcObject = null;
          }
          try { v.remove(); } catch (_) {}
        });
        localContainerRef.current.innerHTML = "";
      } catch (_) {}
    }
  };

  // Helper to completely stop native MediaStreamTracks for microphone to release microphone hardware
  const releaseAudioHardware = (track) => {
    if (track) {
      try {
        if (typeof track.getMediaStreamTrack === "function") {
          const raw = track.getMediaStreamTrack();
          if (raw) {
            raw.enabled = false;
            raw.stop();
          }
        }
      } catch (_) {}
      try {
        if (track._mediaStreamTrack) {
          track._mediaStreamTrack.enabled = false;
          track._mediaStreamTrack.stop();
        }
      } catch (_) {}
      try {
        if (track._originMediaStreamTrack) {
          track._originMediaStreamTrack.enabled = false;
          track._originMediaStreamTrack.stop();
        }
      } catch (_) {}
      try {
        if (track._track) {
          track._track.enabled = false;
          track._track.stop();
        }
      } catch (_) {}
      try { track.stop(); } catch (_) {}
      try { track.close(); } catch (_) {}
    }
  };

  // Helper to clean up DOM videos inside container and release any media stream tracks
  const cleanContainer = () => {
    if (localContainerRef.current) {
      try {
        const vids = localContainerRef.current.querySelectorAll("video");
        vids.forEach((v) => {
          try { v.pause(); } catch (_) {}
          if (v.srcObject) {
            try {
              const stream = v.srcObject;
              if (typeof stream.getTracks === "function") {
                stream.getTracks().forEach((t) => {
                  try { t.enabled = false; } catch (_) {}
                  try { t.stop(); } catch (_) {}
                });
              }
            } catch (_) {}
            v.srcObject = null;
          }
          try { v.remove(); } catch (_) {}
        });
      } catch (_) {}
      try {
        localContainerRef.current.innerHTML = "";
      } catch (_) {}
    }
  };

  // Notify backend on leave (incl. tab close)
  const postLeft = async () => {
    if (!joinedRef.current || !consultationId) return;
    try { await api.post(`/consultations/${consultationId}/left`); } catch (_) {}
    joinedRef.current = false;
  };

  const teardown = async () => {
    try {
      const { audio, video } = localTracksRef.current;
      if (audio) {
        releaseAudioHardware(audio);
      }
      if (video) {
        releaseVideoHardware(video);
      }
      localTracksRef.current = { audio: null, video: null };
      cleanContainer();

      if (clientRef.current) {
        try { await clientRef.current.leave(); } catch (_) {}
        clientRef.current = null;
      }
      setRemoteUsers([]);
      myUidRef.current = null;
    } catch (_) {}
    await postLeft();
  };

  const start = async () => {
    if (isCompleted || !consultationId || phase === "loading" || phase === "joined") return;
    setPhase("loading"); setErrorMsg(""); setRemoteUsers([]);
    try {
      // Teardown any dangling previous client before starting a new session
      if (clientRef.current) {
        try { await clientRef.current.leave(); } catch (_) {}
        clientRef.current = null;
      }

      const { data: t } = await api.post(`/consultations/${consultationId}/agora-token`);
      if (t.mock) { setPhase("mock"); return; }
      const AgoraRTC = (await import("agora-rtc-sdk-ng")).default;
      AgoraRTC.setLogLevel(3);
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      clientRef.current = client;

      const myExpectedUid = t.uid != null && t.uid !== 0 ? String(t.uid) : null;
      myUidRef.current = myExpectedUid;
      const myBase = t.uid ? String(t.uid).slice(0, 4) : null;

      const isSelfOrSameRole = (remoteUid) => {
        if (!remoteUid) return false;
        const rStr = String(remoteUid);
        const myUidStr = myUidRef.current ? String(myUidRef.current) : (client?.uid != null ? String(client.uid) : null);
        if (myUidStr && rStr === myUidStr) return true;
        if (myBase && rStr.startsWith(myBase)) return true;
        return false;
      };

      client.on("connection-state-change", (s) => setConnState(s));

      client.on("user-published", async (remoteUser, mediaType) => {
        try {
          if (!client || client.connectionState !== "CONNECTED") return;
          if (isSelfOrSameRole(remoteUser.uid)) {
            return;
          }
          await client.subscribe(remoteUser, mediaType);
          if (mediaType === "audio") {
            try {
              remoteUser.audioTrack?.play();
            } catch (_) {}
          }
          setRemoteUsers((prev) => {
            const cleanPrev = prev.filter((u) => !isSelfOrSameRole(u.uid));
            const exists = cleanPrev.some((u) => String(u.uid) === String(remoteUser.uid));
            if (exists) {
              return cleanPrev.map((u) => (String(u.uid) === String(remoteUser.uid) ? remoteUser : u));
            }
            return [...cleanPrev, remoteUser];
          });
        } catch (subErr) {
          console.warn("[Agora] subscribe skipped:", subErr?.message);
        }
      });

      client.on("user-unpublished", (remoteUser, mediaType) => {
        if (mediaType === "video") {
          setRemoteUsers((prev) =>
            prev.map((u) => (String(u.uid) === String(remoteUser.uid) ? { ...u, videoTrack: null } : u))
          );
        }
      });

      client.on("user-left", (remoteUser) => {
        setRemoteUsers((prev) => prev.filter((u) => String(u.uid) !== String(remoteUser.uid)));
      });

      // Join with role-based unique session UID (Doctor=1001xxxx, Patient=2001xxxx, Clinic=3001xxxx)
      const baseUid = t.uid !== undefined && t.uid !== null && t.uid !== 0 ? Number(t.uid) : null;
      const sessionUid = baseUid ? (baseUid * 10000 + Math.floor(Math.random() * 9000) + 1000) : null;
      const joinedUid = await client.join(
        t.app_id,
        t.channel,
        t.token || null,
        sessionUid
      );
      myUidRef.current = String(joinedUid || sessionUid || client.uid);
      setRemoteUsers((prev) => prev.filter((u) => !isSelfOrSameRole(u.uid)));

      // Create microphone and camera tracks in a single unified permission request
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
        {},
        { encoderConfig: "720p_1" }
      );
      localTracksRef.current = { audio: audioTrack, video: videoTrack };
      
      await client.publish([audioTrack, videoTrack]);

      joinedRef.current = true;
      setCamOn(true);
      setMicOn(true);
      try { await api.post(`/consultations/${consultationId}/joined`); } catch (_) {}

      setPhase("joined");
    } catch (e) {
      console.error("[Agora] join failed", e);
      const msg = (e && (e.response?.data?.detail || e.message || e.toString())) || "Failed to join call";
      let friendly = msg;
      if (/completed/i.test(msg)) {
        friendly = "cannot join - appointment completed";
      } else if (/permission|NotAllowed|denied/i.test(msg)) {
        friendly = "Browser blocked camera/microphone access. Please allow camera in your browser settings and retry.";
      } else if (/NotFound|Device/i.test(msg)) {
        friendly = "No camera/microphone detected on this device.";
      } else if (/CAN_NOT_GET_GATEWAY_SERVER|invalid token|dynamic key/i.test(msg)) {
        friendly = "Agora server rejected the token. Verify Agora keys in Admin → Integrations.";
      }
      setErrorMsg(friendly);
      setPhase("error");
      await teardown();
    }
  };

  // Auto-start when canShow becomes true
  useEffect(() => {
    if (!isCompleted && canShow && consultationId && phase === "idle") {
      start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShow, consultationId, isCompleted]);

  // Ensure local video track is played in localContainerRef whenever phase is 'joined' and camOn is true
  useEffect(() => {
    if (phase === "joined" && camOn && localTracksRef.current.video && localContainerRef.current) {
      try {
        localTracksRef.current.video.stop();
      } catch (_) {}
      if (localContainerRef.current) {
        localContainerRef.current.innerHTML = "";
      }
      try {
        localTracksRef.current.video.play(localContainerRef.current, { fit: "cover" });
      } catch (err) {
        console.warn("[Agora] local video play error:", err);
      }
    }
  }, [phase, camOn]);

  // Cleanup on unmount + tab close
  useEffect(() => {
    const onBeforeUnload = () => { postLeft(); };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMic = async () => {
    const t = localTracksRef.current.audio;
    if (!t) return;
    const next = !micOn;
    try {
      if (typeof t.setEnabled === "function") {
        await t.setEnabled(next);
      }
      if (typeof t.setMuted === "function") {
        await t.setMuted(!next);
      }
      setMicOn(next);
    } catch (err) {
      console.warn("Toggle mic error:", err);
      setMicOn(next);
    }
  };

  const toggleCam = async () => {
    const t = localTracksRef.current.video;
    if (!t) return;
    const next = !camOn;
    try {
      if (next) {
        // Turning camera ON
        await t.setEnabled(true);
        await t.setMuted(false);
        setCamOn(true);
        if (localContainerRef.current) {
          try { t.stop(); } catch (_) {}
          localContainerRef.current.innerHTML = "";
          t.play(localContainerRef.current, { fit: "cover" });
        }
      } else {
        // Turning camera OFF
        try { t.stop(); } catch (_) {}
        if (localContainerRef.current) {
          localContainerRef.current.innerHTML = "";
        }
        await t.setEnabled(false);
        await t.setMuted(true);
        setCamOn(false);
      }
    } catch (err) {
      console.warn("Toggle cam error:", err);
      setCamOn(next);
    }
  };

  const leaveCall = async () => {
    await teardown();
    setPhase("idle");
    onEndCall?.();
  };

  const conn = CONN_BADGE[connState] || CONN_BADGE.CONNECTING;

  return (
    <section
      data-testid="consult-video-panel"
      className="lg:col-span-3 bg-foreground text-background rounded-2xl overflow-hidden relative aspect-video lg:aspect-auto lg:min-h-[60vh] flex flex-col"
    >
      <div className="flex-1 relative bg-gradient-to-br from-[#0a1a2e] to-[#101a30] flex items-center justify-center p-2 min-h-0 overflow-hidden">
        {isCompleted ? (
          <div className="text-center text-white/80 px-6 max-w-md">
            <Video className="h-12 w-12 mx-auto mb-3 text-white/60" />
            <div className="text-base font-semibold text-white">cannot join - appointment completed</div>
            <div className="text-xs mt-1.5 text-white/60">
              This consultation has already ended.
            </div>
          </div>
        ) : (
          <>
            {phase === "joined" && (
              <div className="w-full h-full flex items-center justify-center">
                {remoteUsers.length === 0 && (
                  <div className="text-center text-white/70 text-sm flex flex-col items-center gap-2">
                    <Users className="h-10 w-10 text-white/40 animate-pulse" />
                    <span>Waiting for other participants to join…</span>
                  </div>
                )}

                {remoteUsers.length === 1 && (
                  <div className="w-full h-full">
                    <RemoteVideoBox user={remoteUsers[0]} />
                  </div>
                )}

                {remoteUsers.length === 2 && (
                  <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 gap-2">
                    {remoteUsers.map((u) => (
                      <RemoteVideoBox key={u.uid} user={u} />
                    ))}
                  </div>
                )}

                {remoteUsers.length >= 3 && (
                  <div className="w-full h-full grid grid-cols-2 gap-2">
                    {remoteUsers.map((u) => (
                      <RemoteVideoBox key={u.uid} user={u} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {phase === "loading" && (
              <div className="text-center text-white/80">
                <Loader2 className="h-7 w-7 mx-auto mb-2 animate-spin" />
                <div className="text-sm">Connecting to video…</div>
              </div>
            )}

            {phase === "mock" && (
              <div className="text-center text-white/80 px-6 max-w-md">
                <Video className="h-12 w-12 mx-auto mb-3 text-white/60" />
                <div className="text-sm font-medium text-white">Video room in mock mode</div>
                <div className="text-xs mt-1 opacity-80">
                  Set Agora App ID + Certificate in Admin → Integrations to enable real video calls.
                </div>
              </div>
            )}

            {phase === "error" && (
              <div className="text-center px-6 max-w-md" data-testid="consult-video-error">
                {/completed/i.test(errorMsg) ? (
                  <>
                    <Video className="h-12 w-12 mx-auto mb-3 text-white/60" />
                    <div className="text-base font-semibold text-white">cannot join - appointment completed</div>
                    <div className="text-xs mt-1.5 text-white/60">
                      This consultation has already ended.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/20 text-rose-200 text-xs font-medium"><AlertTriangle className="h-3.5 w-3.5" /> Connection error</div>
                    <p className="mt-3 text-sm text-white/90">{errorMsg}</p>
                    <div className="mt-4 flex justify-center gap-2">
                      <Button size="sm" variant="outline" onClick={start} data-testid="consult-video-retry" className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white">Retry</Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {phase === "idle" && !canShow && (
              <div className="text-center text-white/70 px-6">
                <Video className="h-10 w-10 mx-auto mb-3 text-white/60" />
                <div className="text-sm">Video will start once the consultation begins.</div>
              </div>
            )}
          </>
        )}

        {/* Self-view (PiP) */}
        {phase === "joined" && (
          <div
            data-testid="consult-local-video"
            className="absolute bottom-4 right-4 w-32 h-20 sm:w-44 sm:h-28 rounded-xl border-2 border-white/30 overflow-hidden bg-slate-900 z-20 shadow-2xl transition-all duration-300 flex items-center justify-center relative"
          >
            <div
              ref={localContainerRef}
              className={`w-full h-full absolute inset-0 [&>div]:!w-full [&>div]:!h-full [&>video]:!w-full [&>video]:!h-full [&>video]:!object-cover transition-opacity duration-200 ${
                camOn ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
              }`}
            />
            {!camOn && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-white/70 p-2 bg-slate-900 z-10">
                <CameraOff className="h-5 w-5 text-rose-400" />
                <span className="text-[10px] font-medium tracking-tight">Camera Off</span>
              </div>
            )}
            <div className="absolute bottom-1 left-1.5 bg-black/70 backdrop-blur px-1.5 py-0.5 rounded text-[9px] text-white/90 font-medium z-20 flex items-center gap-1 pointer-events-none">
              <span className={`w-1.5 h-1.5 rounded-full ${camOn ? "bg-emerald-400" : "bg-rose-400"}`} />
              You
            </div>
          </div>
        )}

        {/* Top HUD */}
        <div className="absolute top-3 left-3 flex items-center gap-2 text-xs z-20">
          <span data-testid="consult-conn-state" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur text-white">
            <span className={"h-1.5 w-1.5 rounded-full " + conn.dot} /> {conn.label}
          </span>
          {startedAt && (
            <span data-testid="consult-timer" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur text-white font-mono tabular-nums">
              {fmtClock(elapsed)}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 p-3 bg-black/70 backdrop-blur border-t border-white/10">
        <button
          type="button"
          data-testid="consult-mic-btn"
          onClick={toggleMic}
          disabled={phase !== "joined"}
          className={"h-11 w-11 rounded-full flex items-center justify-center transition-all disabled:opacity-50 " + (micOn ? "bg-white/10 hover:bg-white/20 text-white" : "bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/30")}
          title={micOn ? "Mute mic" : "Unmute mic"}
        >
          {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </button>
        <button
          type="button"
          data-testid="consult-cam-btn"
          onClick={toggleCam}
          disabled={phase !== "joined"}
          className={"h-11 w-11 rounded-full flex items-center justify-center transition-all disabled:opacity-50 " + (camOn ? "bg-white/10 hover:bg-white/20 text-white" : "bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/30")}
          title={camOn ? "Turn camera off" : "Turn camera on"}
        >
          {camOn ? <Camera className="h-5 w-5" /> : <CameraOff className="h-5 w-5" />}
        </button>
        {phase === "joined" && (
          <button
            type="button"
            data-testid="consult-end-call-btn"
            onClick={leaveCall}
            className="h-11 px-5 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center gap-2 text-sm font-semibold shadow-lg shadow-rose-500/30 transition-all"
            title="Leave call"
          >
            <PhoneOff className="h-4 w-4" /> End call
          </button>
        )}
      </div>
    </section>
  );
}


